import { defineStore } from 'pinia'
import { reactive, ref, watch } from 'vue'
import useValidate from '@/shared/lib/validate'
import { useTranslate } from '@/shared/lib'
import {
  MapLayerPermissions,
  type RoleNotificationPermissionsType,
  type RolePermissionsType,
  type RoleStatusSubscription,
  type RoleTabsPermissionsType,
  type TypePermission,
  type PassportPermission,
  useLoadRole,
} from '@/entities/Role'
import { assign } from 'lodash'

const useModel = defineStore('role_edit_model', () => {
  const { required } = useValidate()

  const { trs } = useTranslate()

  const listPermissions = ref<RolePermissionsType[]>([])
  const listTabs = ref<RoleTabsPermissionsType[]>([])
  const listNotifications = ref<RoleNotificationPermissionsType[]>([])
  const listLayers = [
    { permission: MapLayerPermissions.ALTERNATIVE_MAP, label: 'alternative_map' },
    { permission: MapLayerPermissions.SCHEME, label: 'map_types.scheme' },
    { permission: MapLayerPermissions.SATELLITE, label: 'map_types.satellite' },
    { permission: MapLayerPermissions.HYBRID, label: 'map_types.hybrid' },
  ]

  /* Данные формы */
  const fields = {
    id: null,
    group_id: 0,
    name: '',
    permissions: [],
    tabs_preset: [],
    notification_preset: [] as string[],
    status_subscription_defaults: [] as RoleStatusSubscription[],
    type_permissions: [] as TypePermission[],
    element_permissions: [] as TypePermission[],
    passport_permissions: [] as PassportPermission[],
    map_layers_permissions: [] as string[],
  }

  let data = reactive({ ...fields })

  const cloneStatusSubscriptions = (items: unknown): RoleStatusSubscription[] => {
    if (!Array.isArray(items)) return []

    return items.map((item: any) => ({
      object_type_id: Number(item?.object_type_id),
      status_triggers: Array.isArray(item?.status_triggers) ? [...item.status_triggers] : [],
      is_active: Boolean(item?.is_active),
      schedule: item?.schedule
        ? {
            type: item.schedule.type,
            interval_minutes: Number(item.schedule.interval_minutes),
            at_time: item.schedule.at_time,
            weekdays: Array.isArray(item.schedule.weekdays) ? [...item.schedule.weekdays] : [],
            month_day: Number(item.schedule.month_day),
          }
        : undefined,
    })) as RoleStatusSubscription[]
  }

  /* Правила валидации */
  const rules = {
    name: [(value: string) => required(value)],
  }

  /**
   * Функция приведения к дефолтным значениям
   */
  const clearData = () => {
    Object.keys(data).forEach((key) => {
      data[key] = fields[key]
    })
  }

  /**
   * Функция загрузки данных профиля
   */
  const loadRole = (id: number) => {
    /* Удаляем старые данные перед загрузкой */
    clearData()

    const api = useLoadRole(id)

    watch(api.data, () => {
      const { data: loadData } = api

      if (loadData.value) {
        if (loadData.value.data) {
          const roleData = loadData.value.data

          assign(data, {
            ...roleData,
            // Backend role payload keeps group inside object_group; map it for form and dependent queries.
            group_id: roleData.group_id || roleData.object_group?.id || 0,
            permissions: Array.isArray(roleData.permissions) ? [...roleData.permissions] : [],
            tabs_preset: Array.isArray(roleData.tabs_preset) ? [...roleData.tabs_preset] : [],
            notification_preset: Array.isArray(roleData.notification_preset)
              ? [...roleData.notification_preset]
              : [],
            status_subscription_defaults: cloneStatusSubscriptions(
              roleData.status_subscription_defaults,
            ),
            type_permissions: Array.isArray(roleData.type_permissions)
              ? [...roleData.type_permissions]
              : [],
            element_permissions: Array.isArray(roleData.element_permissions)
              ? [...roleData.element_permissions]
              : [],
            passport_permissions: Array.isArray(roleData.passport_permissions)
              ? [...roleData.passport_permissions]
              : [],
            map_layers_permissions: roleData.map_layers_permissions || [],
          })
        }

        listPermissions.value = loadData.value.payload.permissions
        listTabs.value = loadData.value.payload.tab_permissions

        const fromBackend = loadData.value.payload.notification_permissions
        if (fromBackend && fromBackend.length) {
          listNotifications.value = fromBackend
        } else {
          listNotifications.value = [
            { code: 'events', name: trs('itf.events') },
            { code: 'tasks', name: trs('itf.tasks') },
            { code: 'report_file', name: trs('itf.reports') },
            { code: 'schedule_file', name: trs('itf.schedule') },
          ]
        }
      }
    })

    return api
  }

  return {
    data,
    rules,
    listPermissions,
    listTabs,
    listNotifications,
    listLayers,

    clearData,
    loadRole,
  }
})

export default useModel
